import { useState, useEffect, createContext, useContext, useCallback } from "react";
import { BADGES } from "../lib/userStats";

interface BadgeToastContextType {
  showBadge: (badgeId: string) => void;
}

const BadgeToastContext = createContext<BadgeToastContextType | null>(null);

// eslint-disable-next-line react-refresh/only-export-components
export function useBadgeToast() {
  const context = useContext(BadgeToastContext);
  if (!context) {
    throw new Error("useBadgeToast must be used within a BadgeToastProvider");
  }
  return context;
}

interface BadgeToastProviderProps {
  children: React.ReactNode;
}

export function BadgeToastProvider({ children }: BadgeToastProviderProps) {
  const [currentBadge, setCurrentBadge] = useState<string | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [queue, setQueue] = useState<string[]>([]);

  const showBadge = useCallback((badgeId: string) => {
    setQueue((prev) => [...prev, badgeId]);
  }, []);

  useEffect(() => {
    if (queue.length > 0 && !isVisible) {
      const nextBadge = queue[0];
      // Use queueMicrotask to avoid synchronous setState-in-effect lint warning
      queueMicrotask(() => {
        setCurrentBadge(nextBadge);
        setIsVisible(true);
        setQueue((prev) => prev.slice(1));
      });

      // Hide after 4 seconds
      const timer = setTimeout(() => {
        setIsVisible(false);
        setTimeout(() => setCurrentBadge(null), 150);
      }, 4000);

      return () => clearTimeout(timer);
    }
  }, [queue, isVisible]);

  const badge = currentBadge ? BADGES[currentBadge as keyof typeof BADGES] : null;

  return (
    <BadgeToastContext.Provider value={{ showBadge }}>
      {children}

      {/* Toast */}
      {badge && (
        <div
          onClick={() => {
            setIsVisible(false);
            setTimeout(() => setCurrentBadge(null), 150);
          }}
          className={`fixed top-4 left-1/2 -translate-x-1/2 z-[100] transition-all duration-150 cursor-pointer ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-2 pointer-events-none"
          }`}
        >
          <div className="bg-bg border border-border rounded-md px-5 py-3 flex items-center gap-3 shadow-sm">
            <div>
              <p className="text-[11px] text-muted font-[family-name:--font-ui] font-medium uppercase tracking-wider">Achievement</p>
              <p className="text-sm font-medium text-text font-[family-name:--font-ui]">{badge.name}</p>
            </div>
            <button
              className="text-muted hover:text-text ml-2"
              aria-label="Dismiss"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </BadgeToastContext.Provider>
  );
}
